const USE_BFF = import.meta.env.VITE_USE_BFF !== 'false';

// Constants for Online Sales Journey
export const JOURNEY_TYPES = {
  TERM_INSURANCE: 'termInsurance',
  // Future journey types can be added here
};
export const JOURNEY_PLAN_NAME = 'Term Insurance';

export const COMPONENT_TYPES = {
  LEAD: 'lead',
  QUOTE: 'quote',
  SUMMARY: 'summary',
  CUSTOMER_VERIFICATION: 'customerVerification',
  // Future component types can be added here
};

// API Configuration
const getBaseURL = () => {
  if (typeof Liferay !== 'undefined' && Liferay.ThemeDisplay) {
    if (USE_BFF) {
       return Liferay.ThemeDisplay.getPortalURL() + '/o/bff-api/proxy';
    } 
    return Liferay.ThemeDisplay.getPortalURL();
  }
  return window.location.origin;
};

export const API_BASE_URL = getBaseURL();

export const getPortalBaseURL = () => {
  if (typeof Liferay !== 'undefined' && Liferay.ThemeDisplay) {
    return Liferay.ThemeDisplay.getPortalURL();
  }
  return window.location.origin ;
};

/** Canonical path for the term journey — all journey steps run on this URL after first continue. */
export const TERM_JOURNEY_PATH = '/term-plan';

/** sessionStorage: failed payment → user chose Reinitiate Payment; keep them on /summary after journey hydrate. */
export const REINITIATE_PAYMENT_SUMMARY_SESSION_KEY = 'rnlic_reinitiatePaymentSummary';

/**
 * Full URL for the term journey page (base URL + /term-plan).
 * Use this when redirecting after the first continue in lead stage 1 so the journey
 * always runs on the same canonical page regardless of where the lead was dropped.
 */
export const getTermJourneyCanonicalUrl = () => {
  const base = typeof Liferay !== 'undefined' && Liferay.ThemeDisplay
    ? Liferay.ThemeDisplay.getPortalURL()
    : window.location.origin;
  return base.replace(/\/$/, '') + TERM_JOURNEY_PATH;
};

// API endpoints
export const API_ENDPOINTS = {
  OAUTH_TOKEN: '/o/oauth2/token',
  SUBMIT_LEAD: '/o/restService/termJourney/submitLead',
  CUSTOMER_VERIFICATIONS: '/o/c/customerverifications/',
  PROPOSAL_DATA: '/o/restService/termJourney/proposalData',
  GET_JOURNEY: '/o/restService/termJourney/getJourney',
  PAN_CONTINUE: '/o/restService/termJourney/customerVerification/panContinue',
  AADHAAR_OTP_GENERATE: '/o/restService/journey/aadhaarOtpGenerate',
  AADHAAR_OTP_VALIDATE: '/o/restService/journey/aadhaarOtpValidation',
  GET_FORM_DEFINITION: '/o/restService/getFormDefinition',
  JOURNEY_MASTERS: '/o/restService/journeyMasters',
  BECOME_AN_ADVISOR: '/o/restService/becomeAnAdvisor',
  QUOTE_INIT: '/o/restService/quote/quoteInit',
  SUBMIT_QUOTE: '/o/restService/quote/submitQuote',
  UPDATE_QUOTE_SUMMARY: '/o/restService/quote/updateQuoteSummary',
  QUOTE_SUMMARY: '/o/restService/quote/quoteSummary',
  GENERATE_PAYMENT_URL: '/o/restService/payment/initiate',
  PAYMENT_INITIATE: '/o/restService/payment/initiate',
  PAYMENT_STATUS: '/o/restService/payment/status',
  PAYMENT_RETURN_CONTEXT: '/o/restService/payment/returnContext',
  PAYMENT_RECEIPT: '/o/restService/payment/receipt',
  PROPOSAL_FORM: '/o/restService/proposal/form',
  CALCULATE_PREMIUM: '/o/restService/premium/calculate',
  CALCULATE_MULTI_PREMIUM: '/o/restService/premium/calculateMulti',
  CALCULATE_BULK_PREMIUM: '/o/restService/premium/calculateBulk',
  PROPOSAL_SUMMARY: '/o/restService/proposalSummary',
  JOURNEY_COMPLETION: '/o/restService/termJourney/journeyCompletion',
  GET_POLICY_STATUS: '/o/restService/journey/getPolicyStatus',
  GENERATE_OTP: '/o/restService/generateOTP',
  VERIFY_OTP: "/o/restService/verifyOTP",
  TERM_JOURNEY_GENERATE_OTP: '/o/restService/termJourney/generateOTP',
  TERM_JOURNEY_VERIFY_OTP: '/o/restService/termJourney/verifyOTP',
  BANK_ACCOUNT_VALIDATE: '/o/restService/journey/bankAccountValidate',
  GET_CONTEXT: 'o/inlic/webService/getContent',
  DOCUMENT_UPLOAD: "/o/restService/journey/documentUpload",
  PIVC_LINK: "/o/restService/journey/createPivcLink",
  PINCODE: '/o/restService/journeyMasters?masterType=getProductPincodes',
};

// Retry constants
export const MAX_RETRY_COUNT = 2;

// HTTP status codes
export const HTTP_STATUS = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
};

// Error messages
export const ERROR_MESSAGES = {
  GENERIC_ERROR: "We couldn't complete this action right now. Please try again in a moment.",
  UNABLE_TO_REFRESH_TOKEN: 'Unable to refresh authentication token.',
  REQUEST_FAILED_AFTER_RETRIES: (retryCount) => `Request failed after ${retryCount} retries.`,
  FIELD_REQUIRED: (fieldName) => `${fieldName} is required.`,
  FORM_LOAD_ERROR: 'Failed to load form structure. Please try again later.',
  FORM_SUBMIT_ERROR: 'Failed to submit form. Please try again.',
};

// Success messages
export const SUCCESS_MESSAGES = {
  FORM_SUBMITTED: 'Your form has been submitted successfully.',
};

// Default premium calculation inputs (fallbacks until BI logic is wired)
export const DEFAULT_BASE_SUM_ASSURED = 6000000;
export const DEFAULT_POLICY_TERM = 25;
export const DEFAULT_PREMIUM_PAYING_TERM = 7;

// Premium payment modes for multi-dimension calculation
// Values must match Excel dropdown/validation values exactly
export const PREMIUM_PAYMENT_MODES = ['Yearly', 'Half-yearly', 'Quarterly', 'Monthly'];

// Plus plan: numeric frequency codes for premiumPaymentModeCode
// Maps string payment mode → number expected by Plus Excel (C31)
export const PREMIUM_PAYMENT_MODE_CODES = {
  'Yearly': 1,
  'Half-yearly': 2,
  'Quarterly': 4,
  'Monthly': 12,
};

// Limited Pay (LP) term options
// Plus: 5, 7, 10, 15, 20. Elite: 5, 7, 10, 15.
export const DEFAULT_LIMITED_PAY_OPTIONS = [5, 7, 10, 15];

/**
 * LP term options to request from API, aligned with Quote UI.
 * Plus: 5, 7, 10, 15, 20. Elite: 5, 7, 10, 15.
 * Applies business rule: PT - PPT >= 9 (and therefore PT >= PPT).
 * @param {string} plan - PLAN_NAMES.PLUS or PLAN_NAMES.ELITE
 * @param {number} [policyTerm] - policy term in years (default 50)
 * @returns {number[]}
 */
export const getLimitedPayOptionsForPlan = (plan, policyTerm = 50) => {
  const pt = Number(policyTerm);
  const maxAllowedPptByPt = Number.isFinite(pt) ? pt - 9 : Number.POSITIVE_INFINITY;
  const baseOptions =
    plan === PLAN_NAMES.PLUS ? [5, 7, 10, 15, 20] : [5, 7, 10, 15];
  return baseOptions.filter((ppt) => ppt <= maxAllowedPptByPt);
};

// Payment term type constants
export const PAYMENT_TERM_TYPE = {
  LIMITED_PAY: 'LP',
  REGULAR_PAY: 'RP',
};

// Super Suraksha plan display names and BI product codes (for multi-dimension and quote)
export const PLAN_NAMES = {
  PLUS: 'Super Suraksha Plus',
  ELITE: 'Super Suraksha Elite',
};
export const PLAN_PRODUCT_CODES = {
  [PLAN_NAMES.PLUS]: 'BI_RNL_Super_Suraksha_Plus_121N148V01_V15',
  [PLAN_NAMES.ELITE]: 'BI_RNL_Super_Suraksha_Elite_121N148V01_V15',
};

/** Life cover sum assured step (rupees) — grid options and workbook align to this increment. */
export const COVER_AMOUNT_RUPEE_STEP = 100_000;

/** Debounce before calling multi-premium API while user types custom cover (ms). */
export const CUSTOM_COVER_PREMIUM_DEBOUNCE_MS = 400;

/** Debounce after rider cover text input before committing and calling premium API (ms). */
export const RIDER_COVER_PREMIUM_DEBOUNCE_MS = 280;

/** Debounce before quote grid prefetch (cover tiers + cover-till-age) to avoid API bursts (ms). */
export const GRID_PREFETCH_DEBOUNCE_MS = 450;

export const MIN_COVER_ELITE = 20_000_000;
/** Minimum cover (sum assured) for Plus product – least option shown is 50 Lakh. */
export const MIN_COVER_PLUS = 5_000_000;

/**
 * Plus product: maximum attained age at end of cover (aligns with
 * ProductEligibilityUtil.MAX_PLUS_COVER_TILL_AGE).
 */
export const MAX_COVER_TILL_AGE_PLUS = 75;

/**
 * Absolute maximum cover-till age from product eligibility
 * (ProductEligibilityUtil.MAX_COVER_AGE).
 */
export const MAX_COVER_TILL_AGE_ABSOLUTE = 85;

/** Minimum policy term in years (eligibility / term journey). */
export const MIN_POLICY_TERM_YEARS = 20;

/** Maximum policy term in years (ProductEligibilityUtil.MAX_POLICY_TERM). */
export const MAX_POLICY_TERM_YEARS = 50;

/**
 * Default cover amount (max option) for initial premium fetch.
 * Matches the highest value from generateCoverAmountOptions in Quote.
 */
export const getDefaultCoverAmount = (maxCoverAmount) => {
  const max = Number(maxCoverAmount);
  const step = COVER_AMOUNT_RUPEE_STEP;
  if (!max || isNaN(max)) return Math.round(50000000 / step) * step;
  return Math.round(max / step) * step;
};

// =====================================================
// Income Range Mapping for BI API
// =====================================================
// BI API expects these codes for @ANNUAL_INCOME field
export const INCOME_RANGE_BI_CODES = {
  UPTO_3_LACS: '4',
  '3_TO_6_LACS': '5',
  '6_TO_10_LACS': '6',
  '10_TO_20_LACS': '7',
  '20_TO_50_LACS': '8',
  ABOVE_50_LACS: '9',
};

// Income ranges in rupees with corresponding BI codes
// Used to determine which range an exact income value falls into
export const INCOME_RANGES = [
  { min: 0, max: 300000, biCode: INCOME_RANGE_BI_CODES.UPTO_3_LACS, label: 'Up To 3 Lacs' },
  { min: 300001, max: 600000, biCode: INCOME_RANGE_BI_CODES['3_TO_6_LACS'], label: '3 Lacs To 6 Lacs' },
  { min: 600001, max: 1000000, biCode: INCOME_RANGE_BI_CODES['6_TO_10_LACS'], label: '6 Lacs To 10 Lacs' },
  { min: 1000001, max: 2000000, biCode: INCOME_RANGE_BI_CODES['10_TO_20_LACS'], label: '10 Lacs To 20 Lacs' },
  { min: 2000001, max: 5000000, biCode: INCOME_RANGE_BI_CODES['20_TO_50_LACS'], label: '20 Lacs To 50 Lacs' },
  { min: 5000001, max: Infinity, biCode: INCOME_RANGE_BI_CODES.ABOVE_50_LACS, label: 'Above 50 Lacs' },
];

/**
 * Get BI code and range label from exact annual income value
 * @param {number|string} income - Annual income in rupees (exact value entered by user)
 * @returns {{ biCode: string, rangeLabel: string }} BI code for API and human-readable range label
 */
export const getIncomeRangeFromValue = (income) => {
  // Parse numeric value from string (remove currency symbols, commas, spaces)
  const numericIncome = typeof income === 'string'
    ? parseFloat(income.replace(/[^0-9.]/g, ''))
    : income;

  // Default to '6' (6 Lacs To 10 Lacs) if invalid - matches backend default
  if (isNaN(numericIncome) || numericIncome <= 0) {
    return { biCode: INCOME_RANGE_BI_CODES['6_TO_10_LACS'], rangeLabel: '6 Lacs To 10 Lacs' };
  }

  // Find the matching range
  const range = INCOME_RANGES.find(r => numericIncome >= r.min && numericIncome <= r.max);

  return range
    ? { biCode: range.biCode, rangeLabel: range.label }
    : { biCode: INCOME_RANGE_BI_CODES.ABOVE_50_LACS, rangeLabel: 'Above 50 Lacs' };
};

// Form names for different journey stages
// These map to DDM Structure names configured in Liferay
const FORM_NAMES = {
  lead: 'Super Suraksha Term Lead Form',
  // Add more stages as needed
  // quote: 'Super Suraksha Term Quote Form',
};

/**
 * Get the form name for a given journey stage
 * @param {string} stage - Journey stage (lead, quote, etc.)
 * @returns {string} Form name configured for the stage
 */
export const getFormName = (stage = 'lead') => {
  return FORM_NAMES[stage.toLowerCase()] || FORM_NAMES.lead;
};

/**
 * Get the site group ID from Liferay ThemeDisplay
 * @returns {number|null} Site group ID or null if not available
 */
export const getSiteGroupId = () => {
  if (typeof Liferay !== 'undefined' && Liferay.ThemeDisplay) {
    return Liferay.ThemeDisplay.getSiteGroupId();
  }
  return null;
};

 export const descriptionMap = {
  "accidental-death": "Provides coverage against accidental death",
  "critical-illness": "Provides coverage against 25 critical illnesses",
  "accidental-death-disability":
    "Provides coverage against accidental death and disability",
  "accidental-death-disability-plus":
    "Provides coverage against accidental death and disability",
};

export const knowMore = {
  "accidental-death":
    " INL Accidental Death Benefit Rider enhances the scope of a life insurance policy, by providing protection against unforeseen accidents. The rider, when attached to your base policy, provides a lump sum benefit to the family members in case of loss of life due to an accident.",
  "critical-illness":
    "INL Critical Illness Rider enables you to get the best treatment available and takes care of the indirect expenses while you recover from a critical illness. The rider, when attached to your policy, provides a lump sum in case of diagnosis of one of the 25 listed critical illnesses.",
  "accidental-death-disability":
    "INL Accidental Death and Disability Rider enhances the scope of your life insurance policy, by providing protection against unforeseen accidents. When added to your policy, this rider provides a lump sum benefit on accidental death and income support in case of accidental disability.",
  "accidental-death-disability-plus":
    "INL Accidental Death and Disability Plus Rider enhances the scope of your life insurance policy, by providing protection against unforeseen accidents. When added to your policy, this rider provides a lump sum benefit on accidental death and income support in case of accidental disability. In addition, all future premiums on the base policy are also waived off in case of total & permanent disability due to accident.",
};

export const SIDE_STEPS = [
  {
    id: "personal",
    label: "Personal Details",
    description: "Accurate details ensure seamless policy issuance.",
  },
  {
    id: "bank",
    label: "Bank Details",
    description: "We need this to verify your account.",
  },
  {
    id: "additional",
    label: "Additional Information",
    description: "This section ensures your policy is compliant and tailored.",
  },
];
export const SIDE_STEPSS = [
  {
    id: "lifestyle",
    label: "Lifestyle Details",
    description: "We need this info to know about your healthy lifestyle.",
  },
  {
    id: "medical",
    label: "Medical Details",
    description:
      "Precise information is essential for smooth processing of medical policies.",
  },
];
export const TEXT_CONTENT={

  TRANSGENDER_LEAD:"As per the Product Guidelines, you are not eligible for the current term plan however you can apply for a pension plan or other traditional or savings plans.",

  PERSONALIZE_TEXT:"Let’s personalise your plan!",

  LOCATION:"Where do you live?",

  CHECK:"Final Check!",

  FALL_BACK_MSG:"As per the Product Guidelines, you not eligible to take the current term plan however can apply for a pension plan or other traditional or savings plans.",

  LOCATION_DET:"Location details are required to verify service coverage and risk zone classification.",

  BANNER_HEADING:"IndusInd Nippon Life Super Suraksha Plans",

  TOOLTIP_TEXT:"Base Sum assured in case of unfortunate demise",
  
  COVER_AGE:"Choose longest cover term and freeze your premium rate for lifetime",

  PRE_PAY_TERM:"Ensure savings in total Amount paid against Regular Pay",

  SECTION_DESCRI:"Receive all premiums paid upon survival until maturity with the Return of Premium option",

  SECTION_DESCRIPTION:"Increase your coverage at key life milestones without the need for additional underwriting",

  PLAN_MODEL:"Please review your profile details or contact our support team for more information.",

  MODEL_SUCCESS:"By enabling this option, you can get all your premium payments back at the end of the policy term, providing a free of cost life insurance to you. Kindly read the product brochure for the applicable terms and conditions.",

  MODEL_ENHANCED:"While your current life coverage may appear adequate, it may not fully align with your needs in the future. To help you stay ahead, we offer the Enhanced Coverage Benefit Option, allowing you to increase your coverage at important life milestones like marriage, birth/adoption of child or if a home loan is taken. Kindly read the product brochure for the applicable terms and conditions.",

  MODEL_SUBMIT:"Your quote has been submitted successfully. Our team will contact you shortly.",

  DECLARATION_TEXT:"I declare that I have understood the Benefit Illustration of the proposed plan before entering into the contract.",

  DECLARATION_TEXTT:"I understand and agree to follow the Terms of Use.",

  DECLARATION_TEXTTT:"I further understand that the information I submit will be used as described in the Privacy Policy.",

  AUTO_RENEWAL:"Your plan renews automatically—no missed payments, no policy lapses. Stay insured every single day.",

  VERIFICATION_TITLE:"Make upcoming forms quicker with smart pre-fill",

  VERIFICATION_SUBTITLE:"You can share Aadhaar details to auto-fill your information in the next steps.",

  AADHAR_VERIFI:"Aadhaar verification is done securely using UIDAI-approved services. Your details are masked and never stored.",

  BENEFIT_LIST:"Automatically fills proposer details in the upcoming form",

  BENEFIT_LISTT:"Reduces manual typing and errors",

  BENEFIT_LISTTT:"Makes the proposal process quicker and smoother",

  BENEFIT_FOOTER:"You can skip this step and enter details manually if you prefer.",

  SIDE_TITLE:"Complete your personal details in just a few steps!",

  SIDE_SUBTITLE:"Providing accurate details helps us tailor your policy to your specific needs and ensures a smooth setup.",

  PRO_TOGAL:"Communication address same as permanent address",

  PRO_TOGALL:"Resident of Life Assured for Tax purposes in Jurisdiction(s) outside India",

  DOC_CHOOSE:"Select the documents you will upload as proof for verification.",

  ADDI_DETAILS:"We need to gather some more details! This helps us tailor our services to meet your expectations on a hassle-free process",

  TOGGLE_ONE:"Have you ever been or currently being investigated, charge sheeted, prosecuted or convicted or acquittal or having pending charges in respect of any criminal/civil offences in any court of law in India or abroad?",

  TOGGLE_TWO:"Whether the Life to be Insured/Proposer/Nominee(s)/Appointee(s) is/are Politically Exposed Person(s)?",

  TOLTIP_TEXT:"Politically Exposed Persons (PEPs) are individuals who are or have been entrusted with prominent public functions. PEP norms may also be applied to the accounts of the family members or close relatives of the above referred individuals.",

  TOGGLE_THREE:"Any existing or applied Insurance Coverage?",

  TOGGLE_FOUR:"Any application refused, withdrawn, postponed, declined or offered with revised benefits/rated up or made any claim under any insurance policy?",

  NOM_HEADING:"To honour your claims, we need nominee details. Our team is here to help!",

  NOM_SUBHEADING:"Precise information helps customize your policy, making the process easier.",

  NOM_TOGGLE:"Nominee Address same as Life assured",

  NOMINEE_TOGGLE:"Communication address same as permanent address",

  MED_SUBHEADING:"Precise information helps customize your policy, making the process easier.",

  MED_HEADING:"Accurate lifestyle info helps us to evaluate your policy and ensures a smooth claims process.",

  MED_TOGGLE_ONE:"Do you consume or ever have consumed tobacco?",

  MED_TOGGLE_TWO:"Have you consumed Alcohol in the last one year?",

  MED_TOGGLE_THREE:"Have any of your parents / siblings suffered from any major health conditions?",

  MED_TOOLTIP:"Heart problems, diabetes, stroke, hypertension, raised cholesterol, cancer, or any hereditary disease",
  
  MED_TOOFLE_FOUR:"Do you or have you consumed Narcotics or recreational drugs?",

  MED_TOGGLE_FIVE:"Are you currently or do you intend on engaging in any hazardous occupation or hobbies?",

  MED_TOOLTIPP:"Working at heights, underground or offshore, using explosives, flying other than as a fare-paying passenger, diving, mountaineering or any other dangerous activity?",

  MED_TOOGLE_SIX:"Are you currently or do you intend to live or travel outside of India for more than 6 months?",
}
